import { Row } from '@tanstack/react-table';
import { DataTable, DataTableColumnDef } from 'components/data-table';
import { useAtom } from 'jotai';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { formatMutationResource } from 'utils/misc';
import { parseUTCdateTime } from 'utils/dates';
import { format0 } from 'utils/number';
import { capitalizeFirstLetter } from 'utils/strings';
import { wellLandingAnalysisJobsQueryAtom } from '../atoms';

import styles from './landing-analysis-accordion.module.scss';
import { WellLandingAnalysesTable } from './well-landing-analyses-table';

// ─── Types ────────────────────────────────────────────────────────────────────

export interface WellLandingRequestRow extends Record<string, unknown> {
  requestId: number;
  jobId: string;
  status: string;
  runType: string;
  totalWells: number;
  flaggedWellbores: number;
  runErrors: number;
  inclinationCutoff: string;
  stepLength: string;
  thicknessCutoff: string;
  usesPerforation: string;
  runDate: string;
  wellsQueryHref: string;
}

// ─── Column Definitions ───────────────────────────────────────────────────────

const WELL_LANDING_REQUEST_COLUMNS: DataTableColumnDef<WellLandingRequestRow>[] =
  [
    {
      id: 'jobId',
      accessorKey: 'jobId',
      header: 'Job ID',
      size: 120,
      meta: { type: 'text' },
    },
    {
      id: 'status',
      accessorKey: 'status',
      header: 'Execution Status',
      size: 130,
      meta: { type: 'text' },
    },
    {
      id: 'runType',
      accessorKey: 'runType',
      header: 'Run Type',
      size: 130,
      meta: { type: 'text' },
    },
    {
      id: 'totalWells',
      accessorKey: 'totalWells',
      header: 'Wells',
      size: 110,
      meta: { type: 'integer' },
    },
    {
      id: 'flaggedWellbores',
      accessorKey: 'flaggedWellbores',
      header: 'Flagged Wellbores',
      size: 160,
      meta: { type: 'integer' },
    },
    {
      id: 'runErrors',
      accessorKey: 'runErrors',
      header: 'Run Errors',
      size: 110,
      meta: { type: 'integer' },
    },
    {
      id: 'inclinationCutoff',
      accessorKey: 'inclinationCutoff',
      header: 'Inclination Cutoff',
      size: 155,
      meta: { type: 'text' },
    },
    {
      id: 'stepLength',
      accessorKey: 'stepLength',
      header: 'Well Segment Stop Frequency',
      size: 120,
      meta: { type: 'text' },
    },
    {
      id: 'thicknessCutoff',
      accessorKey: 'thicknessCutoff',
      header: 'Maximum Thickness',
      size: 155,
      meta: { type: 'text' },
    },
    {
      id: 'usesPerforation',
      accessorKey: 'usesPerforation',
      header: 'Uses Perforation Geometry',
      size: 210,
      meta: { type: 'text' },
    },
    {
      id: 'runDate',
      accessorKey: 'runDate',
      header: 'Run Date',
      size: 160,
      meta: { type: 'text' },
    },
  ];

// ─── Component ────────────────────────────────────────────────────────────────

interface WellLandingRequestsTableProps {
  collectionId: string;
  requestsHref: string;
  /** Increment to trigger a reload to page 1 */
  reloadKey?: number;
}

export const WellLandingRequestsTable = ({
  collectionId,
  requestsHref,
  reloadKey,
}: WellLandingRequestsTableProps) => {
  const [requestsQueryState, fetchRequests] = useAtom(
    wellLandingAnalysisJobsQueryAtom
  );
  const { loading: isLoadingRequests, data: requestsData } =
    formatMutationResource(requestsQueryState);
  const requestsError =
    requestsQueryState.result?.type === 'error' ||
    requestsQueryState.result?.type === 'fatal'
      ? 'Failed to load requests'
      : null;

  const [requestsSearch, setRequestsSearch] = useState('');
  const [columnFilters, setColumnFilters] = useState<
    { id: string; value: unknown }[]
  >([]);

  const loadRequestsPage = useCallback(
    ({
      page,
      pageSize,
      text,
    }: {
      page: number;
      pageSize: number;
      text?: string;
    }) => {
      fetchRequests({
        url: requestsHref,
        type: 'POST',
        data: {
          structureGridCollectionId: Number(collectionId),
          scope: ['collection', 'auto_update'],
          page,
          pageSize,
          ...(text ? { text } : {}),
        },
      });
    },
    [collectionId, fetchRequests, requestsHref]
  );

  useEffect(() => {
    loadRequestsPage({
      page: 1,
      pageSize: 50,
      text: requestsSearch || undefined,
    });
  }, [loadRequestsPage, reloadKey, requestsSearch]);

  const requests: WellLandingRequestRow[] = (requestsData?.results ?? []).map(
    (j) => ({
      requestId: j.id,
      jobId: `JOB-${j.id}`,
      status: capitalizeFirstLetter(j.status),
      runType: capitalizeFirstLetter(j.runType),
      totalWells: j.totalWells,
      flaggedWellbores: j.flaggedWellboresCount,
      runErrors: j.runErrorsCount,
      inclinationCutoff: `${j.inclinationCutoffDeg} DEG`,
      stepLength: `${j.stepLengthFt} ft`,
      thicknessCutoff: `${format0(j.thicknessCutoffFt)} ft`,
      usesPerforation: j.usePerforationGeometry ? 'Yes' : 'No',
      runDate: parseUTCdateTime(j.runDate),
      wellsQueryHref: j.resources.queries.wellLandingAnalyses.href,
    })
  );

  const filteredRequests = useMemo(() => {
    if (columnFilters.length === 0) return requests;
    return requests.filter((row) =>
      columnFilters.every((filter) => {
        const value = row[filter.id as keyof WellLandingRequestRow];
        return String(value ?? '')
          .toLowerCase()
          .includes(String(filter.value ?? '').toLowerCase());
      })
    );
  }, [requests, columnFilters]);

  const renderRequestExpansion = (row: Row<WellLandingRequestRow>) => (
    <WellLandingAnalysesTable
      requestId={row.original.requestId}
      wellsQueryHref={row.original.wellsQueryHref as string}
      requestStatus={row.original.status}
      expectedResultCount={row.original.totalWells}
    />
  );

  return (
    <div className={styles.jobsTable}>
      <DataTable<WellLandingRequestRow>
        data={filteredRequests}
        columns={WELL_LANDING_REQUEST_COLUMNS}
        getRowId={(row) => String(row.requestId)}
        isLoading={isLoadingRequests}
        error={requestsError}
        searchPlaceholder="Search requests..."
        scroll={{
          mode: 'paginated',
          serverDriven: true,
          totalCount: requestsData?.totalCount ?? 0,
        }}
        onPaginationChange={({ page, pageSize }) =>
          loadRequestsPage({
            page,
            pageSize,
            text: requestsSearch || undefined,
          })
        }
        onGlobalSearchChange={(s) => setRequestsSearch(s.globalFilter)}
        onFilterChange={(s) => setColumnFilters(s.columnFilters)}
        renderRowSubComponent={renderRequestExpansion}
        config={{
          columnResizing: true,
          sorting: true,
          globalSearch: true,
          columnFilters: true,
          columnVisibility: true,
          columnPinning: true,
          columnReordering: true,
        }}
      />
    </div>
  );
};
